import { createContext, useContext, useEffect } from 'react';
import { useLocalStorageState } from '../shared/db.js';
import { getAppTitle } from '../i18n/i18n.js';
import { LANGUAGE_STORAGE_KEY, DIR_STORAGE_KEY, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../shared/constants.js';

const getBrowserLanguage = () => navigator.language || navigator.userLanguage;

const getDirForLanguage = (lang) => {
    const locale = new Intl.Locale(lang);
    return locale.getTextInfo().direction;
};

const sanitizedBrowserLanguage = Object.keys(SUPPORTED_LANGUAGES).includes(getBrowserLanguage())
    ? getBrowserLanguage()
    : DEFAULT_LANGUAGE;

export const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useLocalStorageState(LANGUAGE_STORAGE_KEY, sanitizedBrowserLanguage);
    const [dir, setDir] = useLocalStorageState(DIR_STORAGE_KEY, getDirForLanguage(sanitizedBrowserLanguage));
    const otherLanguage = Object.keys(SUPPORTED_LANGUAGES).find(lang => lang !== language);

    useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = dir;
        document.title = getAppTitle(language);
    }, [language, dir]);

    const labelForLanguage = (lang) => SUPPORTED_LANGUAGES[lang]?.label || lang;
    const selectLanguage = (selection) => {
        if (Object.keys(SUPPORTED_LANGUAGES).includes(selection)) {
            setLanguage(selection);
            setDir(getDirForLanguage(selection));
        }
    };

    return (
        <LanguageContext.Provider value={{ language, dir, otherLanguage, labelForLanguage, selectLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);
